/**
 * @note This children component use self state, lifecycle and fetching data from store, please be careful for handling the state
 */

// React
import { useEffect, useState, useCallback, useMemo } from 'react';

// Components
import {
  BaseDialog,
  BaseImage,
  BaseLabelStatus,
  FormLabel,
  FormSelectOption,
  FormTextField,
  RoleMultipleSelectOption,
} from 'components';

// Interfaces
import { IUserDetailDialogProps } from './interfaces';
import { IUserUpdateForm, IUserValidateEmail } from 'interfaces/back-office/user';

// Utils
import { messageUtils_error } from 'utils/common/message.utils';
import { commonUtils_handleSelectOptionValue } from 'utils/common/common.utils';

// i18n
import { useTranslation } from 'react-i18next';

// Lodash
import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';

// Debounce Promise
import debounce from 'debounce-promise';

// Antd
import { Col, Form, Row, Spin } from 'antd';

// Custom Hook
import { useValidation, useRequestSaver, useUser, useMasterRole, useMasterCompany, useMasterField } from 'hooks';

// Constant
import { USER_STATUS, STATUS } from 'constant/shared.constant';
import { REQUEST } from 'constant/request.constant';

// Mobx
import { observer } from 'mobx-react';

// UUID
import { v4 as uuidv4 } from 'uuid';

const UserDetailDialog = observer(
  ({
    isOpen,
    isEditable,
    loading,
    isCreate,
    noNik,
    onClickFirstButton,
    onClickSecondButton,
    onClickCloseButton,
    attrs,
  }: IUserDetailDialogProps) => {
    // Hook
    const { t } = useTranslation();
    const { validation_validate, validation_required, validation_validEmail } = useValidation();
    const [form] = Form.useForm();
    const { masterRole_loading, masterRole_dropdown, masterRole_fetchDropdown } = useMasterRole();
    const {
      masterCompany_loading,
      masterCompany_dropdown,
      masterCompany_detail,
      masterCompany_fetchDetail,
      masterCompany_fetchDropdown,
    } = useMasterCompany();
    const { masterField_isLoading, masterField_dropdown, masterField_CLEAR_FIELD_DROPDOWN, masterField_fetchFieldDropdown } =
      useMasterField();
    const { requestSaver_setCancelToken, requestSaver_cancel } = useRequestSaver();
    const { user_loading, user_validateEmail, user_download } = useUser();
    const [isFormLoading, setIsFormLoading] = useState<boolean>(false);
    const inputStyle = useMemo(() => {
      return {
        display: isEditable ? '' : 'none',
      };
    }, [isEditable]);

    // Set default value for the form
    useEffect(() => {
      if (isOpen && !isCreate && !loading.isDetailLoading && isEditable) setDefaultValueForm();

      // eslint-disable-next-line
    }, [isOpen, isCreate, loading.isDetailLoading, isEditable]);

    // Watch any change when user creating bulk user
    useEffect(() => {
      if (isOpen && isCreate) setBulkUserDefaultValueForm();

      // eslint-disable-next-line
    }, [isOpen, isCreate]);

    // Do when user closing modal
    useEffect(() => {
      if (!isOpen) {
        masterField_CLEAR_FIELD_DROPDOWN();

        form.resetFields();

        requestSaver_cancel(REQUEST.BACK_OFFICE_MASTER_DATA_COMPANY_FETCH_DROPDOWN_REQUEST);
        requestSaver_cancel(REQUEST.BACK_OFFICE_MASTER_DATA_COMPANY_FETCH_DETAIL_REQUEST);
        requestSaver_cancel(REQUEST.BACK_OFFICE_MASTER_DATA_ROLE_FETCH_DROPDOWN_REQUEST);
        requestSaver_cancel(REQUEST.BACK_OFFICE_MASTER_DATA_FIELD_FETCH_DROPDOWN_REQUEST);
      }

      // eslint-disable-next-line
    }, [isOpen]);

    /**
     * @description Get company dropdown
     *
     * @return {Promise<void>} Promise<void>
     */
    const getCompanyDropdown = useCallback(async (): Promise<void> => {
      try {
        await masterCompany_fetchDropdown({
          requestConfig: { ...requestSaver_setCancelToken(REQUEST.BACK_OFFICE_MASTER_DATA_COMPANY_FETCH_DROPDOWN_REQUEST) },
        });
      } catch (err) {
        attrs.closeModalCallback && attrs.closeModalCallback();
      }
    }, [attrs, masterCompany_fetchDropdown, requestSaver_setCancelToken]);

    /**
     * @description Get field dropdown
     *
     * @return {Promise<void>} Promise<void>
     */
    const getFieldDropdown = useCallback(async (): Promise<void> => {
      try {
        await masterField_fetchFieldDropdown({
          requestConfig: { ...requestSaver_setCancelToken(REQUEST.BACK_OFFICE_MASTER_DATA_FIELD_FETCH_DROPDOWN_REQUEST) },
        });
      } catch (err) {
        attrs.closeModalCallback && attrs.closeModalCallback();
      }
    }, [attrs, masterField_fetchFieldDropdown, requestSaver_setCancelToken]);

    /**
     * @description Get company detail
     *
     * @param {number} companyId
     *
     * @return {Promise<void>} Promise<void>
     */
    const getCompanyDetail = useCallback(
      async (companyId: number): Promise<void> => {
        try {
          await masterCompany_fetchDetail({
            params: { id: companyId },
            requestConfig: { ...requestSaver_setCancelToken(REQUEST.BACK_OFFICE_MASTER_DATA_COMPANY_FETCH_DETAIL_REQUEST) },
          });
        } catch (_) {
          attrs.closeModalCallback && attrs.closeModalCallback();
        }
      },
      [masterCompany_fetchDetail, requestSaver_setCancelToken, attrs],
    );

    /**
     * @description Get role dropdown
     *
     * @return {Promise<void>} Promise<void>
     */
    const getRoleDropdown = useCallback(async (): Promise<void> => {
      try {
        await masterRole_fetchDropdown({
          requestConfig: { ...requestSaver_setCancelToken(REQUEST.BACK_OFFICE_MASTER_DATA_ROLE_FETCH_DROPDOWN_REQUEST) },
        });
      } catch (err) {
        attrs.closeModalCallback && attrs.closeModalCallback();
      }

      // eslint-disable-next-line
    }, []);

    /**
     * @description Set default value form
     *
     * @return {Promise<void>} Promise<void>
     */
    const setDefaultValueForm = useCallback(async (): Promise<void> => {
      getRoleDropdown();
      await getFieldDropdown();
      await getCompanyDropdown();
      await getCompanyDetail(attrs.user?.companyId!);

      form.setFieldsValue({
        ...form.getFieldsValue(),
        ...attrs.user,
        accountStatusName: attrs.user?.accountStatus ? 'Aktif' : 'Tidak aktif',
        companyId: commonUtils_handleSelectOptionValue(masterCompany_dropdown, attrs.user?.companyId),
        branchId: commonUtils_handleSelectOptionValue(masterCompany_detail?.branch, attrs.user?.branchId),
        fieldId: commonUtils_handleSelectOptionValue(masterField_dropdown, attrs.user?.fieldId),
        roles: attrs.user?.roles?.map(role => {
          return { role: role.id?.toString(), roleName: role.name };
        }),
      });
    }, [
      attrs.user,
      form,
      getCompanyDetail,
      getCompanyDropdown,
      getFieldDropdown,
      getRoleDropdown,
      masterCompany_detail?.branch,
      masterCompany_dropdown,
      masterField_dropdown,
    ]);

    /**
     * @description Set Bulk User Default Value Form
     *
     * @return {Promise<void>} Promise<void>
     */
    const setBulkUserDefaultValueForm = useCallback(async (): Promise<void> => {
      getRoleDropdown();
      await getCompanyDropdown();
      await getFieldDropdown();

      if (attrs.userDynamic) {
        form.setFieldsValue({
          ...form.getFieldsValue(),
          ...attrs.userDynamic,
          roles: attrs?.userDynamic?.roleIds?.map(roleId => ({ role: roleId?.toString() })),
        });

        // Load the company detail
        getCompanyDetail(attrs.userDynamic.companyId!);
      } else {
        form.setFieldsValue({
          ...form.getFieldsValue(),
          roles: [undefined],
        });
      }
    }, [attrs.userDynamic, form, getCompanyDetail, getCompanyDropdown, getFieldDropdown, getRoleDropdown]);

    /**
     * @description Watch any change in company dropdown
     *
     * @param {string} companyId
     *
     * @return {Promise<void>} Promise<void>
     */
    // eslint-disable-next-line
    const onChangeCompany = useCallback(
      async (companyId: string): Promise<void> => {
        if (!companyId) {
          form.setFieldsValue({
            ...form.getFieldsValue(),
            branchId: undefined,
          });

          return;
        }

        try {
          await getCompanyDetail(parseInt(companyId));

          form.setFieldsValue({
            ...form.getFieldsValue(),
            branchId: commonUtils_handleSelectOptionValue(masterCompany_detail?.branch, attrs?.user?.branchId),
          });
        } catch (err) {
          //
        }
      },
      // eslint-disable-next-line
      [form, masterCompany_detail?.branch, masterField_dropdown, attrs?.user?.fieldId],
    );

    /**
     * @description Watch any change when form is submitted
     *
     * @param {IUserUpdateForm} form
     *
     * @return {void} void
     */
    const onFinish = useCallback(
      (form: IUserUpdateForm): void => {
        if (isCreate) {
          const mapForm = {
            ...form,
            id: uuidv4(),
            companyName: masterCompany_dropdown?.find(company => company.value === form.companyId?.toString())?.label,
            fieldName: masterField_dropdown?.find(division => division.value === form.fieldId?.toString())?.label,
          };

          onClickFirstButton(mapForm);

          return;
        }

        if (attrs.user.status === USER_STATUS.APPROVED) onClickFirstButton(form);
        else onClickSecondButton && onClickSecondButton();
      },
      [attrs.user.status, isCreate, masterCompany_dropdown, masterField_dropdown, onClickFirstButton, onClickSecondButton],
    );

    /**
     * @description Check before emitting the onClickFirstButton method
     *
     * @return {void} void
     */
    const onBeforeClickSecondButton = useCallback(async (): Promise<any> => {
      setIsFormLoading(true);

      try {
        const response = await form.validateFields();
        const clonedResponse = cloneDeep({
          ...omit(response, ['roles']),
          roleIds: response?.roles?.map((role: any) => parseInt(role?.role)) as number[],
        }) as IUserUpdateForm;

        onFinish(clonedResponse);
      } catch (err: any) {
        return messageUtils_error(t('app.validations.formNeedValidation'));
      } finally {
        setIsFormLoading(false);
      }
    }, [form, onFinish, t]);

    /**
     * @description Download
     *
     * @param {string} blobId
     * @param {string} originalFileName
     *
     * @return {Promise<void>} Promise<void>
     */
    const onDownload = useCallback(
      async (blobId: string, originalFileName: string): Promise<void> => {
        try {
          await user_download({
            params: { blobId, originalFileName },
            requestConfig: {},
          });
        } catch (_) {
          //
        }
      },
      [user_download],
    );

    /**
     * @description Render body
     *
     * @return {JSX.Element} JSX.Element
     */
    const renderBody = useCallback((): JSX.Element => {
      return (
        <Spin spinning={loading.isDetailLoading}>
          <Form
            form={form}
            name="user-detail-dynamic-form"
            onFinish={onBeforeClickSecondButton}
            scrollToFirstError
            autoComplete="off"
            requiredMark={false}
          >
            {/* Name */}
            <Row className="mb-4">
              <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                <FormLabel label="user.form.name" />
              </Col>
              <Col xs={24} sm={24} md={14} lg={14} xl={14}>
                <Form.Item
                  name="name"
                  rules={validation_validate(isCreate ? ['required', 'alphaNumeric'] : [''], 'user.form.name')}
                  style={inputStyle}
                >
                  <FormTextField placeholder="user.formPlaceholder.name" readOnly={!isCreate} />
                </Form.Item>
                {!isEditable && <FormLabel label={attrs.user.name || ''} />}
              </Col>
            </Row>

            {/* Email */}
            <Row className="mb-4">
              <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                <FormLabel label="user.form.email" />
              </Col>
              <Col xs={24} sm={24} md={14} lg={14} xl={14}>
                <Form.Item
                  name="email"
                  rules={[
                    validation_required('user.form.email'),
                    validation_validEmail(),
                    {
                      validator: debounce(async (_: any, value: any) => {
                        // Check when bulk inserting user
                        // Check if dynamic user object exists
                        // This validation for validating email and does not require server
                        if (isCreate) {
                          const isEmailExists = attrs?.dynamicForms?.some(dynamicForm => dynamicForm?.email?.includes(value));
                          const dynamicUser = attrs?.dynamicForms?.find(dynamicForm => dynamicForm.id === attrs?.userDynamic?.id);
                          if (!attrs?.userDynamic && isEmailExists)
                            return Promise.reject(new Error(t('app.validations.emailRegistered')));
                          if (dynamicUser && isEmailExists && attrs.userDynamic?.id !== dynamicUser.id)
                            return Promise.reject(new Error(t('app.validations.emailRegistered')));
                        }

                        if (value) {
                          try {
                            const response = (await user_validateEmail({
                              params: { email: value },
                              body: { userId: attrs.user.id || null },
                              requestConfig: {
                                ...requestSaver_setCancelToken(REQUEST.BACK_OFFICE_USER_MANAGEMENT_VALIDATE_EMAIL_REQUEST),
                              },
                            })) as IUserValidateEmail;

                            // Check if email is registered and not self email
                            if (response.isEmailRegistered && !response.isSelf)
                              return Promise.reject(new Error(t('app.validations.emailRegistered')));
                            // Check if email is not registered in OA
                            if (!response.isOaEmail) return Promise.reject(new Error('app.validations.notOaEmail'));
                            form.setFieldsValue({
                              ...form.getFieldsValue(),
                              npk: response.npk,
                              companyId: response.companyId?.toString(),
                            });
                          } catch (err: any) {
                            return Promise.reject(new Error(err.message));
                          }
                          return Promise.resolve();
                        }
                      }, 2000),
                    },
                  ]}
                  style={inputStyle}
                >
                  <FormTextField
                    placeholder="user.formPlaceholder.email"
                    readOnly={!isCreate || user_loading.isActionLoading}
                    loading={user_loading.isActionLoading}
                    isEmail
                  />
                </Form.Item>
                {!isEditable && <FormLabel label={attrs.user.email || ''} />}
              </Col>
            </Row>

            {/* NPK */}
            <Row className="mb-4">
              <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                <FormLabel label="user.form.npk" />
              </Col>
              <Col xs={24} sm={24} md={14} lg={14} xl={14}>
                <Form.Item
                  name="npk"
                  rules={validation_validate(isCreate ? ['required', 'numeric'] : [], 'user.form.npk')}
                  style={inputStyle}
                >
                  <FormTextField placeholder="user.formPlaceholder.npk" readOnly={!isCreate} />
                </Form.Item>
                {!isEditable && <FormLabel label={attrs.user.npk || ''} />}
              </Col>
            </Row>

            {/* Company */}
            <Row className="mb-4">
              <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                <FormLabel label="user.form.company" />
              </Col>
              <Col xs={24} sm={24} md={14} lg={14} xl={14}>
                <Form.Item
                  name="companyId"
                  rules={validation_validate([isCreate ? 'required' : ''], 'user.form.company')}
                  style={inputStyle}
                >
                  <FormSelectOption
                    list={masterCompany_dropdown}
                    loading={masterCompany_loading.isDropdownLoading}
                    disabled={masterCompany_loading.isDropdownLoading || !isCreate}
                    // onChange={value => onChangeCompany(value)}
                    placeholder="user.formPlaceholder.company"
                  />
                </Form.Item>
                {!isEditable && <FormLabel label={attrs.user.companyName || ''} />}
              </Col>
            </Row>

            {/* Branch */}
            {!isCreate && (
              <Row className="mb-4">
                <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                  <FormLabel label="user.form.branch" />
                </Col>
                <Col xs={24} sm={24} md={14} lg={14} xl={14}>
                  <Form.Item
                    name="branchId"
                    rules={validation_validate([isCreate ? 'required' : ''], 'user.form.branch')}
                    style={inputStyle}
                  >
                    <FormSelectOption
                      list={masterCompany_detail?.branch}
                      loading={masterCompany_loading.isDropdownLoading}
                      disabled={masterCompany_loading.isDropdownLoading || !isCreate}
                      placeholder="user.formPlaceholder.branch"
                    />
                  </Form.Item>
                  {!isEditable && <FormLabel label={attrs.user.branchName || ''} />}
                </Col>
              </Row>
            )}

            {/* Division */}
            <Row className="mb-4">
              <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                <FormLabel label="app.user.companyData.form.field" />
              </Col>
              <Col xs={24} sm={24} md={14} lg={14} xl={14}>
                <Form.Item
                  name="fieldId"
                  rules={validation_validate([isCreate ? 'required' : ''], 'app.user.companyData.formPlaceholder.field')}
                  style={inputStyle}
                >
                  <FormSelectOption
                    list={masterField_dropdown}
                    loading={masterField_isLoading}
                    disabled={masterField_isLoading || !isCreate}
                    placeholder="app.user.companyData.formPlaceholder.field"
                  />
                </Form.Item>
                {!isEditable && <FormLabel label={attrs.user.fieldName || ''} />}
              </Col>
            </Row>

            {/* NIK */}
            {!noNik && (
              <Row className="mb-4">
                <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                  <FormLabel label="user.form.nik" />
                </Col>
                <Col xs={24} sm={24} md={14} lg={14} xl={14}>
                  <Form.Item
                    name="nik"
                    rules={validation_validate(isCreate ? ['required', 'numeric'] : [''], 'user.form.nik')}
                    style={inputStyle}
                  >
                    <FormTextField placeholder="user.formPlaceholder.nik" readOnly={!isCreate} />
                  </Form.Item>
                  {!isEditable && <FormLabel label={attrs.user.nik || ''} />}
                </Col>
              </Row>
            )}

            {/* User Status */}
            {!isEditable && (
              <Row className="mb-4">
                <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                  <FormLabel label="user.form.status" />
                </Col>
                <Col xs={24} sm={24} md={14} lg={14} xl={14}>
                  <BaseLabelStatus isUserStatus value={attrs.user.status || ''} />
                </Col>
              </Row>
            )}

            {/* Images */}
            {!isEditable && (
              <>
                {/* ID Card */}
                <Row className="mb-4">
                  <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                    <FormLabel label="user.form.idCard" />
                  </Col>
                  <Col xs={24} sm={24} md={14} lg={14} xl={14}>
                    {attrs.user.idCardPhotoBlobId ? (
                      <div
                        className="flex items-center cursor-pointer"
                        onClick={() =>
                          onDownload(
                            attrs.user.idCardPhotoBlobId!,
                            `LSP-${attrs.user.name?.split(' ').join('-')}-${t('user.form.idCard')?.split(' ')?.join('-')}`,
                          )
                        }
                      >
                        <BaseImage isIcon isSvg icon="back-office/icon-download-primary" />
                        <FormLabel label={'app.download'} />
                      </div>
                    ) : (
                      '-'
                    )}
                  </Col>
                </Row>

                {/* Bachelor Certificate Photo */}
                <Row className="mb-4">
                  <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                    <FormLabel label="user.form.bachelorCertificatePhoto" />
                  </Col>
                  <Col xs={24} sm={24} md={14} lg={14} xl={14}>
                    {attrs.user.bachelorCertificatePhotoBlobId ? (
                      <div
                        className="flex items-center cursor-pointer"
                        onClick={() =>
                          onDownload(
                            attrs.user.bachelorCertificatePhotoBlobId!,
                            `LSP-${attrs.user.name?.split(' ').join('-')}-${t('user.form.bachelorCertificatePhoto')
                              ?.split(' ')
                              ?.join('-')}`,
                          )
                        }
                      >
                        <BaseImage isIcon isSvg icon="back-office/icon-download-primary" />
                        <FormLabel label={'app.download'} />
                      </div>
                    ) : (
                      '-'
                    )}
                  </Col>
                </Row>

                {/* Family Card Photo */}
                <Row className="mb-4">
                  <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                    <FormLabel label="user.form.familyCardPhoto" />
                  </Col>
                  <Col xs={24} sm={24} md={14} lg={14} xl={14}>
                    {attrs.user.familyCardPhotoBlobId ? (
                      <div
                        className="flex items-center cursor-pointer"
                        onClick={() =>
                          onDownload(
                            attrs.user.familyCardPhotoBlobId!,
                            `LSP-${attrs.user.name?.split(' ').join('-')}-${t('user.form.familyCardPhoto')
                              ?.split(' ')
                              ?.join('-')}`,
                          )
                        }
                      >
                        <BaseImage isIcon isSvg icon="back-office/icon-download-primary" />
                        <FormLabel label={'app.download'} />
                      </div>
                    ) : (
                      '-'
                    )}
                  </Col>
                </Row>

                {/* Work Card Photo */}
                <Row className="mb-4">
                  <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                    <FormLabel label="user.form.workCardPhoto" />
                  </Col>
                  <Col xs={24} sm={24} md={14} lg={14} xl={14}>
                    {attrs.user.workCardPhotoBlobId ? (
                      <div
                        className="flex items-center cursor-pointer"
                        onClick={() =>
                          onDownload(
                            attrs.user.workCardPhotoBlobId!,
                            `LSP-${attrs.user.name?.split(' ').join('-')}-${t('user.form.workCardPhoto')?.split(' ')?.join('-')}`,
                          )
                        }
                      >
                        <BaseImage isIcon isSvg icon="back-office/icon-download-primary" />
                        <FormLabel label={'app.download'} />
                      </div>
                    ) : (
                      '-'
                    )}
                  </Col>
                </Row>
              </>
            )}

            {/* ROLES */}
            {(attrs.user.status === USER_STATUS.APPROVED || isCreate) && (
              <Row className="mb-4">
                <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                  <FormLabel label="user.form.roles" />
                </Col>
                <Col xs={24} sm={24} md={14} lg={14} xl={14}>
                  <div style={inputStyle}>
                    <RoleMultipleSelectOption
                      loading={masterRole_loading.isDropdownLoading}
                      formName="roles"
                      form={form}
                      roleDropdown={masterRole_dropdown}
                    />
                  </div>

                  {!isEditable && (
                    <FormLabel
                      label={attrs.user.roles
                        ?.map(role => role.name)
                        ?.toString()
                        ?.split(',')
                        ?.join(', ')}
                    />
                  )}
                </Col>
              </Row>
            )}

            <hr />
            <div className="mt-4">
              <p className="font-weight-700 font-20 mt-4 mb-4 mx-0">{t('user.form.accountStatus')}</p>

              {/* Account Status */}
              <Row className="mb-4">
                <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                  <FormLabel label="user.form.accountStatus" />
                </Col>
                <Col xs={24} sm={24} md={14} lg={14} xl={14}>
                  <Form.Item
                    name="accountStatus"
                    rules={validation_validate(['required'], 'user.form.accountStatus')}
                    style={inputStyle}
                  >
                    <FormSelectOption list={STATUS} placeholder="user.formPlaceholder.accountStatus" />
                  </Form.Item>
                  {!isEditable && <FormLabel label={t(`app.status.${String(attrs.user.accountStatus)}`)} />}
                </Col>
              </Row>
            </div>
          </Form>
        </Spin>
      );
    }, [
      attrs?.dynamicForms,
      attrs.user.accountStatus,
      attrs.user.bachelorCertificatePhotoBlobId,
      attrs.user.branchName,
      attrs.user.companyName,
      attrs.user.email,
      attrs.user.familyCardPhotoBlobId,
      attrs.user.fieldName,
      attrs.user.id,
      attrs.user.idCardPhotoBlobId,
      attrs.user.name,
      attrs.user.nik,
      attrs.user.npk,
      attrs.user.roles,
      attrs.user.status,
      attrs.user.workCardPhotoBlobId,
      attrs.userDynamic,
      form,
      inputStyle,
      isCreate,
      isEditable,
      loading.isDetailLoading,
      masterCompany_detail?.branch,
      masterCompany_dropdown,
      masterCompany_loading.isDropdownLoading,
      masterField_dropdown,
      masterField_isLoading,
      masterRole_dropdown,
      masterRole_loading.isDropdownLoading,
      noNik,
      onBeforeClickSecondButton,
      onDownload,
      requestSaver_setCancelToken,
      t,
      user_loading.isActionLoading,
      user_validateEmail,
      validation_required,
      validation_validEmail,
      validation_validate,
    ]);

    return (
      <BaseDialog
        title={`${`user.detail.personalData`}`}
        isOpen={isOpen}
        closable
        body={renderBody()}
        firstButtonLabel={attrs.user.status === USER_STATUS.APPROVED || isCreate ? 'app.save' : 'user.action.rejectRegistration'}
        secondButtonLabel="user.action.approveRegistration"
        onFirstButtonClick={() =>
          attrs.user.status === USER_STATUS.APPROVED || isCreate ? onBeforeClickSecondButton() : onClickFirstButton()
        }
        onSecondButtonClick={onClickSecondButton}
        onButtonCloseClick={() => onClickCloseButton(false)}
        isFirstButtonLoading={
          loading.isDetailLoading ||
          attrs.isUserActionLoading ||
          isFormLoading ||
          masterRole_loading.isDropdownLoading ||
          masterCompany_loading.isDetailLoading ||
          masterCompany_loading.isDropdownLoading
        }
        isSecondButtonLoading={loading.isDetailLoading}
        firstButtonColor={attrs.user.status === USER_STATUS.WAITING_APPROVAL ? 'danger' : ''}
        isUsingSecondButton={attrs.user.status === USER_STATUS.WAITING_APPROVAL}
        width="673px"
        isUsingDefaultFooter
        forceRender
        isUsingButton={
          attrs.user.status === USER_STATUS.WAITING_APPROVAL ||
          isCreate ||
          (attrs.user.status === USER_STATUS.APPROVED && isEditable)
        }
      />
    );
  },
);

export { UserDetailDialog };
